import 'react-tagsinput/react-tagsinput.css';
import './TagsInputField.css';

import get from 'lodash/get';
import uniq from 'lodash/uniq';

import classNames from 'classnames';
import FormErrorMessage from 'components/FormErrorMessage';
import { makeTagsInputIpFieldLayout } from 'components/TagsInputIpField/TagsInputIpFieldLayout';
import { List } from 'immutable';
import { useEffect, useRef, useState } from 'react';
import AutosizeInput from 'react-input-autosize';
import TagsInput from 'react-tagsinput';

const getErrorMessages = (msg, error) => {
  if (Array.isArray(error)) {
    return error.map((err) => (
      <FormErrorMessage
        className='tags-input-field__error-message'
        key={get(err, 'value')}
      >
        {get(msg, ['validation', get(err, 'type')], 'Error').replace(
          '{suffix}',
          get(err, 'value'),
        )}
      </FormErrorMessage>
    ));
  }
  return (
    <FormErrorMessage>
      {get(msg, ['validation', error], 'Error')}
    </FormErrorMessage>
  );
};

const TagsInputField = ({
  block,
  className,
  disabled: disabledField,
  id,
  input,
  meta: { touched, error },
  msg,
  placeholder,
  touch,
  wrapperClassName = '',
}) => {
  const inputRef = useRef(null);
  const layoutRef = useRef(null);
  const [collapsed, setCollapsed] = useState(true);

  const scrollToBottom = () => {
    if (layoutRef.current) {
      layoutRef.current.scrollTop = layoutRef.current.scrollHeight;
    }
  };

  const handleClickOutside = (e) => {
    if (layoutRef.current && !layoutRef.current.contains(e.target)) {
      setCollapsed(true);
      scrollToBottom();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  });

  const onChange = (args) => {
    touch(input.name);
    if (Array.isArray(args)) {
      const cleanedArgs = args
        .map((arg) => arg.trim())
        .filter((arg) => arg !== '');
      input.onChange(new List(uniq(cleanedArgs)));
    } else {
      input.onChange(new List(uniq(args)));
    }
  };

  const renderTag = (props) => {
    const {
      tag,
      key,
      disabled,
      onRemove,
      classNameRemove,
      getTagDisplayValue,
      ...other
    } = props;
    const value = getTagDisplayValue(tag);
    const handleRemove = () => {
      if (!disabled) {
        onRemove(key);
      }
    };
    return (
      <span
        key={key}
        {...other}
        onClick={handleRemove}
        className={classNames(
          other.className,
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        )}
      >
        {value}
        {/* biome-ignore lint/a11y/useAnchorContent: legacy code */}
        {/* biome-ignore lint/a11y/useValidAnchor: legacy code */}
        {!disabled && <a className={classNameRemove} onClick={handleRemove} />}
      </span>
    );
  };

  function autosizingRenderInput({ addTag, ...props }) {
    const { onChange: onChangeInput, value, ...other } = props;
    return (
      <AutosizeInput
        type='text'
        onChange={onChangeInput}
        value={value}
        {...other}
      />
    );
  }

  return (
    <div
      className={classNames(
        block ? 'block' : 'inline-block',
        'TagsInputField',
        'tags-input-field',
        wrapperClassName,
      )}
    >
      <TagsInput
        addOnBlur
        className={classNames(
          'TagsInputField react-tagsinput',
          touched && error && 'error',
          disabledField && 'react-tagsinput--disabled',
          collapsed && 'react-tagsinput--collapsed',
          className,
        )}
        hasError={touched && error}
        id={id}
        inputProps={{ placeholder, onFocus: () => setCollapsed(false) }}
        onChange={onChange}
        onlyUnique
        renderInput={autosizingRenderInput}
        renderTag={renderTag}
        value={
          List.isList(input.value) ? input.value.toJS() : input.value || []
        }
        ref={inputRef}
        renderLayout={makeTagsInputIpFieldLayout(
          inputRef,
          layoutRef,
          scrollToBottom,
        )}
        disabled={disabledField}
      />
      {touched && error && getErrorMessages(msg, error)}
    </div>
  );
};

export default TagsInputField;
